import type {
  AdminTemplateCreatePayload,
  InfobipImportableTemplate,
  InfobipLineOption,
  InfobipTemplateRef,
  WhatsappTemplateCreationInput,
  WhatsappTemplateScope,
} from '../types/adminTemplates';
import type { WhatsappTemplate } from '../types/flows';
import type { AgentInternalLine, NegotiationAgent } from '../types/negotiationAgents';

const INFOBIP_KEY_SEPARATOR = '::';

/** Valor do filtro de linha que mostra todas; o Select do Radix não aceita item com valor vazio. */
export const ALL_INFOBIP_LINES = 'ALL';
/** Templates sem linha vêm do sender do ambiente (importação anterior às linhas por WABA). */
export const DEFAULT_INFOBIP_LINE = 'DEFAULT';

export function infobipTemplateKey(ref: InfobipTemplateRef): string {
  return `${ref.telefoneInternoId ?? ''}${INFOBIP_KEY_SEPARATOR}${ref.name}`;
}

/**
 * Uma chamada de importação por linha: nome de template é único dentro da WABA, não entre WABAs,
 * então o core não teria como decidir de qual conta veio cada nome num lote misto.
 */
export function groupInfobipRefsByLine(
  refs: InfobipTemplateRef[],
): Array<{ telefoneInternoId?: string; templateNames: string[] }> {
  const byLine = new Map<string, string[]>();
  for (const ref of refs) {
    const lineId = ref.telefoneInternoId ?? '';
    byLine.set(lineId, [...(byLine.get(lineId) ?? []), ref.name]);
  }
  return [...byLine.entries()].map(([lineId, templateNames]) => ({
    ...(lineId ? { telefoneInternoId: lineId } : {}),
    templateNames,
  }));
}

/**
 * Agente com um escritório só leva o template para esse escritório; qualquer outro recorte
 * (todos, vários ou nenhum, como o interno) fica global, que é o que todos eles enxergam.
 */
export function whatsappTemplateScopeOf(
  agent: Pick<NegotiationAgent, 'availability' | 'escritorioIds'>,
): WhatsappTemplateScope {
  return agent.availability === 'SELECTED' && agent.escritorioIds.length === 1
    ? { escritorioId: agent.escritorioIds[0] }
    : {};
}

/**
 * Só o agente interno atende linhas. A primeira ativa é a principal: o core ordena por
 * `ativo` e `ordem`, então é ela que responde quando o agente tem mais de uma.
 */
export function preferredAgentLineId(lines: AgentInternalLine[]): string | undefined {
  return lines.find((line) => line.ativo)?.id;
}

/**
 * O core recusa FLOW com escritório e exige escritório no CUSTOM: o tipo segue o escopo. Os dois
 * aparecem no catálogo de templates do agente (global ou do escritório dele).
 */
export function buildWhatsappTemplateCreatePayload({
  payload,
  escritorioId,
  telefoneInternoId,
  finalidadeWhatsapp,
}: WhatsappTemplateCreationInput): AdminTemplateCreatePayload {
  const line = telefoneInternoId ? { telefoneInternoId } : {};
  const finalidade = finalidadeWhatsapp ? { finalidadeWhatsapp } : {};
  return escritorioId
    ? { ...payload, ...line, ...finalidade, canal: 'WHATSAPP', tipo: 'CUSTOM', escritorioId }
    : { ...payload, ...line, ...finalidade, canal: 'WHATSAPP', tipo: 'FLOW' };
}

function lineFilterValue(template: InfobipImportableTemplate): string {
  return template.telefoneInternoId ?? DEFAULT_INFOBIP_LINE;
}

function lineLabel(template: InfobipImportableTemplate): string {
  if (!template.telefoneInternoNumero) return 'Número padrão do ambiente';
  return template.telefoneInternoTipo
    ? `${template.telefoneInternoNumero} · ${template.telefoneInternoTipo}`
    : template.telefoneInternoNumero;
}

/**
 * Opções do filtro derivadas da própria listagem. A linha preferida entra mesmo sem template:
 * senão o filtro abriria num valor sem opção, e quem chegou pela linha nem saberia que está vazia.
 */
export function infobipLineOptions(
  templates: InfobipImportableTemplate[],
  preferredTelefoneInternoId?: string,
): InfobipLineOption[] {
  const options = new Map<string, string>();
  for (const template of templates) options.set(lineFilterValue(template), lineLabel(template));
  if (preferredTelefoneInternoId && !options.has(preferredTelefoneInternoId))
    options.set(preferredTelefoneInternoId, 'Linha selecionada (sem templates)');
  return [
    { value: ALL_INFOBIP_LINES, label: 'Todas as linhas' },
    ...[...options.entries()].map(([value, label]) => ({ value, label })),
  ];
}

export function filterInfobipTemplates(
  templates: InfobipImportableTemplate[],
  search: string,
  line: string,
): InfobipImportableTemplate[] {
  const term = search.trim().toLowerCase();
  return templates.filter(
    (template) =>
      (line === ALL_INFOBIP_LINES || lineFilterValue(template) === line) &&
      (!term ||
        template.name.toLowerCase().includes(term) ||
        (template.bodyText?.toLowerCase().includes(term) ?? false) ||
        (template.telefoneInternoNumero?.toLowerCase().includes(term) ?? false)),
  );
}

/**
 * O template aparece no nó assim que ganha a finalidade, mas só envia depois de aprovado na Meta
 * em toda WABA do tipo. O core manda `null` no nó por linha (sem tipo no pedido) e quando não há
 * linha do tipo: a aprovação não está em jogo ali, então nada de selo, como sem o campo.
 */
const templatePendenteNaMeta = ({
  situacaoNaFinalidade,
}: Pick<WhatsappTemplate, 'situacaoNaFinalidade'>): boolean =>
  situacaoNaFinalidade != null && situacaoNaFinalidade !== 'APROVADO';

/** O SearchableSelect só exibe o rótulo da opção: o selo "Pendente na Meta" vai no próprio texto. */
export function rotuloDoTemplateNoNo(template: WhatsappTemplate): string {
  const nome = template.whatsappTemplateName
    ? `${template.nome} (${template.whatsappTemplateName})`
    : template.nome;
  return templatePendenteNaMeta(template) ? `${nome} · Pendente na Meta` : nome;
}
